import axios from "axios";
import entityService from "@/services/entityService";
import entity from "@/services/entity";
import Api from "../../services/Api";
import router from "../../router/index";
import { createDebouncedLatest } from "@/utils/debouncedLatest";

/**
 * Quiet period before an availability probe is sent (CL-18307).
 *
 * Exported so a test can assert the action is debounced at all without
 * hard-coding a duration, and so the value is named rather than being a bare
 * literal buried in a store module.
 */
export const SUBDOMAIN_CHECK_DEBOUNCE_MS = 400;

/**
 * The one availability prober for the whole app (CL-18307).
 *
 * Module scope, not per-action-call: the three Add Company screens
 * (passport_account/CreateCompany.vue, modal/CreateCompanyModal.vue,
 * account/register.vue) all dispatch `checkEntitySubdomain`, and only a single
 * shared instance can collapse a typing burst into one request and keep the
 * newest answer as the only one that reaches the store.
 */
const subdomainProbe = createDebouncedLatest({
  delay: SUBDOMAIN_CHECK_DEBOUNCE_MS,
  run: (params, { onCancel }) => {
    // axios 0.21 predates AbortSignal, so cancellation is a CancelToken. This
    // is what stops a superseded probe from staying on the wire: without it a
    // slow request for an abandoned value keeps a connection and a server
    // worker busy even though its answer is already guaranteed to be discarded.
    const source = axios.CancelToken.source();
    onCancel(() => source.cancel("superseded by a newer subdomain check"));

    return entityService.checkEntitySubdomain(params.subdomain, params.server, {
      cancelToken: source.token,
    });
  },
});

export const state = {
  loading: false,
  isValidSubdomain: false,
  subdomain: null,
  entities: [],
  modalState: false,
  entityStatus: null,
};

export const actions = {
  checkDomain({ commit }, params) {
    return new Promise((reject) => {
      entityService
        .checkSubdomain(params)
        .then((data) => {
          commit("setSubdomain", data.data.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  /**
   * Check whether a subdomain is available (CL-18307).
   *
   * Resolves an OUTCOME, not the raw axios response:
   *
   *   { result: <axios response>, payload }  the newest call's answer
   *   { superseded: true, payload }          a call the user typed past
   *
   * Callers MUST check `superseded` and leave their UI untouched for those.
   * A superseded call is not a negative answer -- treating it as one is how
   * the field would flash "already used" at a value the user never submitted.
   *
   * Three defects fixed here, all reported on CL-18307:
   *
   *   1. One request per keystroke. The probe is now debounced, so a burst of
   *      typing produces a single settled request.
   *
   *   2. Unbounded retry. The old catch block called this action's function
   *      object directly on any 401 -- `actions.checkEntitySubdomain(...)` --
   *      which recursed with no attempt limit and was a large part of the
   *      ~3-requests-per-keystroke the reporter measured. It is gone: ClientApi
   *      now acquires the token BEFORE the request and retries once itself, so
   *      there is nothing left for this layer to retry.
   *
   *   3. `err.response.status` on an undefined response. A network failure, a
   *      timeout and a cancellation all produce an error with no `response` at
   *      all, so the handler meant to deal with errors threw a TypeError of its
   *      own -- the "error handlers failing on undefined responses" on the
   *      ticket. The rejection is now passed through untouched.
   */
  checkEntitySubdomain({ commit }, params) {
    commit("authfack/loadongStatus", true, { root: true });

    return subdomainProbe.schedule(params).then((outcome) => {
      // Superseded: a newer value is already being checked. Committing here is
      // exactly the stale-response race -- the store (and therefore every
      // screen reading `getIsValidSubdomain`) would show the verdict for a
      // value that is no longer in the input.
      if (outcome.superseded) {
        return outcome;
      }

      const status = outcome.result && outcome.result.data && outcome.result.data.data;

      // `loadongStatus` handling is deliberately left as it was, including the
      // fact that it is only cleared on an available subdomain. It is the
      // GLOBAL authfack loading flag that also gates the login and
      // forgot-password submit buttons, so changing when it clears reaches
      // screens this ticket does not cover. The stuck-flag behaviour is
      // recorded as a separate observation rather than fixed here.
      status && commit("authfack/loadongStatus", false, { root: true });
      commit("setIsValidSubdomain", status);

      return outcome;
    });
  },

  /**
   * Abandon any pending or in-flight availability probe (CL-18307).
   *
   * Dispatched by the Add Company screens when the field stops holding a
   * checkable value -- cleared, or edited into something that fails format
   * validation. Without this, a probe already scheduled for the previous value
   * would still land and paint a verdict beside an empty or invalid box.
   */
  cancelEntitySubdomainCheck() {
    subdomainProbe.cancel();
  },
  checkEntityStatus({ commit, rootGetters }, params) {
    return new Promise((resolve, reject) => {
      entity
        .checkEntityStatus(params.data)
        .then((data) => {
          resolve(data);
          commit("setEntityStatus", data.data);
          if (data.data) {
            const {
              entity: {
                server: { server_url },
                userId,
                entity_id,
              },
            } = params;
            const entityURL = `${server_url}/sso?id=${userId}&atex=${rootGetters["user/getUserData"].atex}&entity_id=${entity_id}&ip=${rootGetters["user/getIp"]}`;
            window.location.href = entityURL;
          } else {
            router.push("/error-revoked");
          }
        })
        .catch((err) => {
          reject(err);
          this._vm.$toast.error(err.data.message);
        });
    });
  },
  createEntity({ commit, dispatch }, params) {
    commit("setLoading", true);
    return new Promise((resolve, reject) => {
      const userId = JSON.parse(localStorage.getItem("user")).data.user.id;
      let timeOut = 3000;
      let pwd = null;
      Api()
        .get(`/list-user-all-entities/${userId}?page=1`)
        .then((data) => {
          const servers = data.data.data;
          const serversApis = [];
          servers.forEach((server) => {
            serversApis.push(server.server.server_api);
          });
          // let userData = null;
            let userData = JSON.parse(localStorage.getItem("user")).data.user;
            userData.exisingUser = true;
            Api()
                .get(`/get-user-pwd/${userId}`)
                .then((d) => {
                    pwd = d.data;
                })
                .catch((err) => {
                    console.log(err);
                });
          if (!serversApis.includes(params.server.server_api)) {
              userData.exisingUser = false;
            // userData = JSON.parse(localStorage.getItem("user")).data.user;
            delete userData.atex;
            delete userData.tmp_pwd;
            delete userData.timezone;
          }
          setTimeout(() => {
            if (userData !== null) userData.password = pwd;
            params.data = userData;
            entityService
              .createEntity(params)
              .then((data) => {
                resolve(data);
                commit("setIsValidSubdomain", true);
                let entityId = data.data.data.id;
                commit("setLoading", false);
                this._vm.$toast.success(this.$t('form.entityCreated'));
                commit("setModalState", true);
                setTimeout(() => {
                  entityService
                    .attachUserToEntity(entityId)
                    .then((data) => {
                      console.log(data);
                      dispatch("user/manageCreateCompanyModal", false, {
                        root: true,
                      });
                      setTimeout(() => {
                        dispatch("entities/fetchUserEntities", null, {
                          root: true,
                        });
                      }, 5000);
                    })
                    .catch((err) => {
                      console.log(err);
                      dispatch("user/manageCreateCompanyModal", false, {
                        root: true,
                      });
                      dispatch("entities/fetchUserEntities", null, {
                        root: true,
                      });
                    });
                }, 1000);
              })
              .catch((error) => {
                reject(error);
                commit("setLoading", false);
                commit("setModalState", false);
                this._vm.$toast.error(
                  Object.values(error.response.data.errors).join("\r\n")
                );
              });
          }, timeOut);
        })
        .catch((error) => {
          console.log(error);
        });
    });
  },
  resetState({ commit }) {
    commit("setLoading", false);
    commit("setModalState", false);
  },
};

export const mutations = {
  setLoading(state, payload) {
    state.loading = payload;
  },
  setIsValidSubdomain(state, payload) {
    state.isValidSubdomain = payload;
  },
  setSubdomain(state, payload) {
    state.subdomain = payload;
  },
  setModalState(state, payload) {
    state.modalState = payload;
  },
  setEntityStatus(state, payload) {
    state.entityStatus = payload;
  },
};

export const getters = {
  getLoading(state) {
    return state.loading;
  },
  getIsValidSubdomain(state) {
    return state.isValidSubdomain;
  },
  getSubdomain(state) {
    return state.subdomain;
  },
  getModalState(state) {
    return state.modalState;
  },
  getEntityStatus(state) {
    return state.entityStatus;
  },
};
